import { Link } from "@tanstack/react-router"
import { Github, Twitter, Linkedin, Mail } from "lucide-react"

const footerLinks = {
  product: [
    { label: "Features", href: "/features" },
    { label: "Integrations", href: "/features/integrations" },
    { label: "AI Gateway", href: "/features/ai-gateway" },
    { label: "Agents Platform", href: "/features/agents-platform" },
    { label: "Pricing", href: "/info/pricing" },
  ],
  solutions: [
    { label: "API Management", href: "/use-cases/api-management" },
    { label: "AI Automation", href: "/use-cases/ai-automation" },
    { label: "Data Sync", href: "/use-cases/data-synchronization" },
    { label: "FinOps", href: "/use-cases/finops" },
    { label: "Enterprise Security", href: "/use-cases/enterprise-security" },
  ],
  resources: [
    { label: "Concepts", href: "/concepts" },
    { label: "Comparisons", href: "/comparisons" },
    { label: "API Gateway Guide", href: "/concepts/api-gateway" },
    { label: "AI Agents Guide", href: "/concepts/ai-agents" },
    { label: "Documentation", href: "/docs" },
  ],
  company: [
    { label: "About", href: "/info/about" },
    { label: "Contact", href: "/info/contact" },
  ],
  legal: [
    { label: "Privacy Policy", href: "/legal/privacy" },
    { label: "Terms of Service", href: "/legal/terms" },
    { label: "Security", href: "/legal/security" },
    { label: "Compliance", href: "/legal/compliance" },
  ]
}

const socialLinks = [
  { icon: Github, href: "https://github.com/matricks-ai", label: "GitHub" },
  { icon: Twitter, href: "https://twitter.com/matricksai", label: "Twitter" },
  { icon: Linkedin, href: "https://linkedin.com/company/matricks", label: "LinkedIn" },
  { icon: Mail, href: "mailto:support@matricks.ai", label: "Email" }
]

export function Footer() {
  return (
    <footer className="border-t bg-muted/30">
      <div className="container px-6 py-12">
        {/* Main Footer Content */}
        <div className="grid grid-cols-2 gap-8 sm:grid-cols-2 lg:grid-cols-7">
          {/* Brand */}
          <div className="col-span-2 lg:col-span-2">
            <Link to="/" className="flex items-center space-x-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary">
                <span className="text-sm font-bold text-primary-foreground">M</span>
              </div>
              <span className="text-xl font-bold">Matricks.ai</span>
            </Link>
            <p className="mt-4 text-sm text-muted-foreground max-w-xs">
              The Secured Enterprise Integration & Agentic AI Platform. Connect, manage, and secure all your APIs and AI agents.
            </p>
            <div className="mt-6 flex space-x-4">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  className="text-muted-foreground hover:text-primary transition-colors"
                  aria-label={social.label}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <social.icon className="h-5 w-5" />
                </a>
              ))}
            </div>
          </div>

          {/* Product */}
          <div>
            <h3 className="text-sm font-semibold">Product</h3>
            <ul className="mt-4 space-y-3">
              {footerLinks.product.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.href}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Solutions */}
          <div>
            <h3 className="text-sm font-semibold">Solutions</h3>
            <ul className="mt-4 space-y-3">
              {footerLinks.solutions.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.href}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h3 className="text-sm font-semibold">Resources</h3>
            <ul className="mt-4 space-y-3">
              {footerLinks.resources.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.href}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="text-sm font-semibold">Company</h3>
            <ul className="mt-4 space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.href}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h3 className="text-sm font-semibold">Legal</h3>
            <ul className="mt-4 space-y-3">
              {footerLinks.legal.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.href}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} Matricks.ai. All rights reserved.
            </p>
            <div className="flex items-center space-x-6 text-sm text-muted-foreground">
              <a href="https://status.matricks.ai" className="hover:text-primary transition-colors" target="_blank" rel="noopener noreferrer">
                Status
              </a>
              <Link to="/legal/security" className="hover:text-primary transition-colors">
                Security
              </Link>
              <Link to="/legal/privacy" className="hover:text-primary transition-colors">
                Privacy
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}
